import { useCallback, useContext, useEffect, useReducer } from 'react'
import {
    getDealerProfilesWithLimiter,
    getUsersRegistrationWithLimiter,
} from '../../../../apiRoutes'
import {
    useAuth,
    usePermissions,
} from '../../../../context/AuthenticationContext'
import { ToastMessage } from '../../../../context/ToastPopUpContext'
import { useGlobalState } from '../../../../context/GlobalStateContext'
import {
    columnMapper,
    permissionsCheck,
    universalDateConverter,
    useDebounce,
} from '../../../../utilities/functionTools'
import {
    Grid,
    UniversalCookieCrumb,
    UniversalDataTable,
    UniversalLoadingCircle,
    UniversalModal,
} from '../../../UniversalComponents'

import ContactsIcon from '@mui/icons-material/Contacts'
import UniversalToolBar from '../../../UniversalComponents/UniversalToolbar'
import DealerProfileForm from './DealerProfileForm'
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

const today = new Date().toISOString().substring(0, 10)

export const DealerProfiles = () => {
    const setToastMessage = useContext(ToastMessage)
    const authentication = useAuth()
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { activeManufacturer, userEmail, userProfile } = oupUser
    const { auth } = authentication
    const { userPreferences } = auth
    const { setUser } = useAuth()

    interface DealerRow {
        id: string | number
        dealerProfileName: string
        manufacturers: Array<{
            manufacturerName: string
        }>
        dealers: Array<{
            dealerName: string
        }>
    }

    const defaultState = {
        open: false,
        row: {},
        data: [],
        viewActive: true,
        loading: false,
        paginationModel: { page: 0, pageSize: 50 },
        apiSearch: '',
        rowCount: 0,
        noResults: false,
        dealerView: {},
        dealers: {
            dealerProfile: {
                columns: [
                    {
                        field: 'dealerProfileName',
                        headerName: 'DEALER PROFILE NAME',
                        width: 300,
                        type: 'string',
                    },
                    {
                        field: 'dealerName',
                        headerName: 'DEALERS',
                        width: 800,
                        sortable: false,
                        filterable: false,
                        renderCell: (params) => {
                            const dealers = params?.row ?? []

                            if (dealers.length === 0) {
                                return (
                                    <Box
                                        component="span"
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        None
                                    </Box>
                                )
                            }

                            return (
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                        alignItems: 'center',
                                        py: 0.5,
                                    }}
                                >
                                    {dealers?.dealers?.map((dealer, index) => (
                                        <Chip
                                            key={index}
                                            label={dealer?.dealerName}
                                            size="medium"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            )
                        },
                    },
                    {
                        field: 'manufacturerName',
                        headerName: 'MANUFACTURES',
                        width: 800,
                        sortable: false,
                        filterable: false,
                        renderCell: (params) => {
                            const mfrs = params?.row ?? []

                            if (mfrs.length === 0) {
                                return (
                                    <Box
                                        component="span"
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        None
                                    </Box>
                                )
                            }

                            return (
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                        alignItems: 'center',
                                        py: 0.5,
                                    }}
                                >
                                    {mfrs?.manufacturers?.map(
                                        (manufacturer, index) => (
                                            <Chip
                                                key={index}
                                                label={
                                                    manufacturer?.manufacturerName
                                                }
                                                size="medium"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )
                                    )}
                                </Stack>
                            )
                        },
                    },
                ] as GridColDef[],
                density: 'standard',
            },
        },
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const userPermissions = usePermissions()
    const {
        open,
        row,
        data,
        density,
        dealers,
        viewActive,
        loading,
        paginationModel,
        apiSearch,
        rowCount,
        noResults,
        dealerView,
    } = state

    const { dealerProfile } = dealers

    const defaultColumns = [
        'dealerProfileName',
        'manufacturers.manufactruerName',
        'dealers.dealerName',
    ]

    const setModalVisbility = async (tableRow: any, newData: any) => {
        if (tableRow?.dealerProfileName) {
            setState({ row: tableRow, open: !open })
        } else {
            setState({ row: defaultState.row, open: !open })
        }
        getData(viewActive)
    }

    const debouncedSearchQuery = useDebounce(apiSearch, 1000)

    const onApiSearch = useCallback((query: any) => {
        setState({
            apiSearch: query,
        })
        if (query !== null) {
            setState({ paginationModel: { ...paginationModel, page: 0 } })
        }
    }, [])

    const getData = async (active) => {
        setState({ loading: true })
        let dealerProfileData: any
        let findDealer: any
        try {
            const dealerProfiles = await getDealerProfilesWithLimiter({
                active,
                page: paginationModel?.page + 1,
                limit: 50,
                filter: debouncedSearchQuery
                    ? JSON.stringify(debouncedSearchQuery)
                    : undefined,
            })

            dealerProfileData = Array.isArray(dealerProfiles?.profiles)
                ? dealerProfiles?.usersReg
                : []
            const total = Number(dealerProfiles?.total) || 0

            if (
                dealerProfileData?.length === 0 &&
                total === 0 &&
                debouncedSearchQuery !== null
            ) {
                setToastMessage(
                    'No Profiles found - Please Try another Again or Add a Dealer Profile',
                    'info'
                )
                setState({
                    apiSearch: null,
                    paginationModel: { page: 0, pageSize: 50 },
                    rowCount: total,
                    loading: false,
                    noResults: true,
                })
                return
            }
            dealerProfileData = columnMapper(
                defaultColumns,
                dealerProfiles?.dealerProfiles
            ).map((item: any) => ({
                ...item,
                id: item?._id,
            }))

            if (userProfile?.dealerProfile === true) {
                findDealer = dealerProfileData.find(
                    ({ _id }) => _id === oupUser?.assignedDealerProfile?._id
                )
            }

            setState({
                data: dealerProfileData,
                rowCount: total,
                loading: false,
                noResults: false,
                dealerView: findDealer,
            })
        } catch (err: any) {
            let errorMessage = 'An unknown error occurred'

            if (err.response?.data?.message) {
                errorMessage = err.response.data.message
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error
            } else if (err.message) {
                errorMessage = err.message
            }
            setState({ data: [], rowCount: 0, users: [] })
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const buttons = [
        {
            buttonName: 'Add Dealer Profile',
            buttonIcon: <ContactsIcon />,
            buttonFunction: () => setState({ open: !open }),
        },
    ]

    useEffect(() => {
        getData(true)
    }, [])

    useEffect(() => {
        if (noResults) {
            return
        } else {
            getData(true)
        }
    }, [paginationModel?.page, debouncedSearchQuery])

    useEffect(() => {
        setState({ paginationModel: { page: 0, pageSize: 50 } })
        getData(true)
    }, [activeManufacturer])

    const isUserDealer = userProfile?.dealerProfile

    if (loading) {
        return (
            <Grid item xs={12} md={12} lg={12}>
                <Typography align="center" sx={{ marginTop: 40 }}>
                    <CircularProgress />
                </Typography>
            </Grid>
        )
    }

    return (
        <Grid container>
            {isUserDealer ? (
                <Grid container>
                    <Grid item xs={12}>
                        <DealerProfileForm
                            formTitle={'Dealer Profile'}
                            row={dealerView}
                            onClose={() => {}}
                            isDealerView={isUserDealer}
                        />
                    </Grid>
                </Grid>
            ) : (
                <Grid container>
                    <Grid item xs={12}>
                        <UniversalToolBar buttons={buttons} />
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                        <UniversalLoadingCircle
                            data={data}
                            reloadFunction={() => getData(true)}
                            customTimeoutMessage={
                                'No Available Dealer Profiles'
                            }
                        >
                            <UniversalDataTable
                                data={data}
                                apiDensity={density}
                                apiColumns={dealers?.dealerProfile?.columns}
                                checkboxSelection={true}
                                onRowSelectionModelChange={(selectionModel) =>
                                    setState({
                                        selectedUserIds: selectionModel,
                                    })
                                }
                                visibleColumns={[]}
                                getRowData={(tableRow) =>
                                    setState({
                                        open: true,
                                        row: tableRow,
                                    })
                                }
                                sx={{ width: 10 }}
                                rowCount={rowCount}
                                paginationModel={paginationModel}
                                loading={loading}
                                paginationMode={'server'}
                                pageSizeOptions={[50]}
                                setPaginationModel={(data) => {
                                    setState({ paginationModel: data })
                                }}
                                onApiSearch={(text) => {
                                    setState({ noResults: false })
                                    onApiSearch(text)
                                }}
                            />
                        </UniversalLoadingCircle>
                    </Grid>
                </Grid>
            )}

            <UniversalModal
                title={'Dealer Profile'}
                open={open}
                onClose={() => setState({ open: false, row: defaultState.row })}
                timeStamp={`${
                    row?.id
                        ? ` Modified By: ${row?.updatedBy} ${
                              row?.updatedAt
                                  ? `| Modified Date ${universalDateConverter(
                                        row?.updatedAt
                                    )}`
                                  : ''
                          }  `
                        : ` Created by: ${userEmail} | Modified Date: ${today} `
                }`}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 10,
                    marginBottom: 5,
                }}
                draggable={true}
                minimizable={true}
            >
                <DealerProfileForm
                    formTitle={'Dealer Profile'}
                    row={row}
                    onClose={() => {
                        setState({ open: false, row: defaultState.row })
                        getData(true)
                    }}
                />
            </UniversalModal>
        </Grid>
    )
}
